/**
 * Estilos de la escena de la botella (ficha y llegada), portados del
 * prototipo aprobado `prototipo-botella.html`. Paleta del concepto
 * «La botella y la bitácora» (F1 §2): azul noche, verde botella, ámbar,
 * crema, sepia, latón y bermellón. Un solo acento: el latón.
 *
 * Lo ambiental (olas, cabeceo, haz del faro) son keyframes CSS; solo
 * `transform` y `opacity`, sin filtros animados. Todo queda quieto con
 * `prefers-reduced-motion`.
 */
import { css, keyframes } from '@emotion/css';

/** Variables de la escena: de día (claro) y de noche (oscuro). */
const DIA = {
  '--e-cielo-a': '#cfdae4',
  '--e-cielo-b': '#efe7d8',
  '--e-astro': '#fbf1dc',
  '--e-astro-halo': 'rgba(251,236,205,.7)',
  '--e-costa': '#9fb0b8',
  '--e-costa-2': '#bccacf',
  '--e-mar-a': '#3d6c77',
  '--e-mar-b': '#173440',
  '--e-mar-c': '#2a5562',
  '--e-mar-d': '#1f4652',
  '--e-cresta': 'rgba(255,255,255,.42)',
  '--e-muelle': '#3a2e24',
  '--e-velo': 'rgba(14,20,36,.25)',
  '--e-velo-2': 'rgba(14,22,34,.62)',
  '--e-papel': '#efe6d3',
  '--e-papel-2': '#ddcfb2',
  '--e-tinta': '#3a2e24',
  '--e-tinta-2': '#6e5c49',
  '--e-laton': '#9c7a3c',
  '--e-laton-2': '#c9a865',
  '--e-rumbo': 'rgba(58,46,36,.13)',
  '--e-rotulo': '#2c2a26',
  '--e-gavi': '#f6f2ea',
  '--e-gavi-ala': '#c9cfd8',
  '--e-gavi-trazo': '#2f3440',
  '--e-faro': '0',
} as const;

const NOCHE = {
  '--e-cielo-a': '#0b0f1a',
  '--e-cielo-b': '#1f2a44',
  '--e-astro': '#ebe3cf',
  '--e-astro-halo': 'rgba(235,227,207,.18)',
  '--e-costa': '#131b2c',
  '--e-costa-2': '#18223a',
  '--e-mar-a': '#18293f',
  '--e-mar-b': '#060b15',
  '--e-mar-c': '#11203a',
  '--e-mar-d': '#0c172b',
  '--e-cresta': 'rgba(190,205,240,.2)',
  '--e-muelle': '#0a0d14',
  '--e-velo': 'rgba(0,0,0,.35)',
  '--e-velo-2': 'rgba(4,7,14,.66)',
  '--e-papel': '#e2d6bd',
  '--e-papel-2': '#c9b994',
  '--e-tinta': '#2b2118',
  '--e-tinta-2': '#5e4d3b',
  '--e-laton': '#c4a062',
  '--e-laton-2': '#dcc08a',
  '--e-rumbo': 'rgba(200,210,235,.07)',
  '--e-rotulo': '#e2d6bd',
  '--e-gavi': '#d9dee8',
  '--e-gavi-ala': '#8f9bb0',
  '--e-gavi-trazo': '#0b0f1a',
  '--e-faro': '1',
} as const;

/** Variables de la escena (el `var(...)` de cada token). */
export const e = {
  cieloA: 'var(--e-cielo-a)',
  cieloB: 'var(--e-cielo-b)',
  astro: 'var(--e-astro)',
  astroHalo: 'var(--e-astro-halo)',
  costa: 'var(--e-costa)',
  costa2: 'var(--e-costa-2)',
  marA: 'var(--e-mar-a)',
  marB: 'var(--e-mar-b)',
  marC: 'var(--e-mar-c)',
  marD: 'var(--e-mar-d)',
  cresta: 'var(--e-cresta)',
  muelle: 'var(--e-muelle)',
  velo: 'var(--e-velo)',
  velo2: 'var(--e-velo-2)',
  papel: 'var(--e-papel)',
  papel2: 'var(--e-papel-2)',
  tinta: 'var(--e-tinta)',
  tinta2: 'var(--e-tinta-2)',
  laton: 'var(--e-laton)',
  laton2: 'var(--e-laton-2)',
  rumbo: 'var(--e-rumbo)',
  rotulo: 'var(--e-rotulo)',
  gavi: 'var(--e-gavi)',
  gaviAla: 'var(--e-gavi-ala)',
  gaviTrazo: 'var(--e-gavi-trazo)',
  faro: 'var(--e-faro)',
} as const;

/** Serif humanista de cartelas y carta (nunca una «manuscrita» falsa). */
export const SERIF =
  '"Iowan Old Style","Palatino Linotype",Palatino,"Noto Serif","Book Antiqua",Georgia,"Liberation Serif",serif';

/**
 * Grano de papel: ruido `feTurbulence` en un SVG de 96 px pintado una vez
 * como imagen de fondo (no se anima; 0 bytes de imagen).
 */
export const GRANO = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96"><filter id="g"><feTurbulence type="fractalNoise" baseFrequency=".9" numOctaves="2" stitchTiles="stitch"/><feColorMatrix values="0 0 0 0 .24 0 0 0 0 .2 0 0 0 0 .14 0 0 0 .09 0"/></filter><rect width="96" height="96" filter="url(#g)"/></svg>',
)}")`;

export const ola = keyframes({ to: { transform: 'translateX(-412px)' } });
const cabeceo = keyframes({
  '0%, 100%': { transform: 'rotate(-1.5deg)' },
  '50%': { transform: 'rotate(1.5deg)' },
});
const vaiven = keyframes({
  '0%, 100%': { transform: 'translateY(-2px)' },
  '50%': { transform: 'translateY(2px)' },
});
const faro = keyframes({
  '0%': { transform: 'rotate(-200deg) scaleX(.15)' },
  '25%': { transform: 'rotate(-180deg) scaleX(1)' },
  '50%': { transform: 'rotate(-160deg) scaleX(.15)' },
  '50.01%': { transform: 'rotate(-20deg) scaleX(.15)' },
  '75%': { transform: 'rotate(0deg) scaleX(1)' },
  '100%': { transform: 'rotate(20deg) scaleX(.15)' },
});

const quieto = {
  '& .e-ambiente': { animation: 'none !important' },
} as const;

/**
 * Raíz de una escena: declara las variables del tema (`.noche` en oscuro)
 * y las animaciones ambientales.
 */
export const claseEscena = css({
  ...DIA,
  '&.noche': NOCHE,
  '& .e-ab': { position: 'absolute' },
  '& .e-full': { position: 'absolute', inset: 0 },
  '& .e-ola': { animation: `${ola} 11s linear infinite` },
  '& .e-ola.rapida': { animationDuration: '7s' },
  '& .e-cabeceo': {
    transformOrigin: '50% 78%',
    animation: `${cabeceo} 7s ease-in-out infinite`,
  },
  '& .e-vaiven': { animation: `${vaiven} 5s ease-in-out infinite` },
  '& .e-haz': {
    transformOrigin: '0 50%',
    background:
      'linear-gradient(90deg,rgba(242,212,138,.42),rgba(242,212,138,0) 80%)',
    clipPath: 'polygon(0 46%,100% 0,100% 100%,0 54%)',
    opacity: `calc(${e.faro} * .9)`,
    animation: `${faro} 9s linear infinite`,
  },
  '& .tnum': { fontVariantNumeric: 'tabular-nums lining-nums' },
  '&.quieta': quieto,
  '@media (prefers-reduced-motion: reduce)': quieto,
});

/** Cresta de una ola repetida (lienzo de 824 de ancho, dos vueltas de 412). */
export function trazoOla(y: number, amplitud: number): string {
  let d = `M0 ${y}Q51.5 ${y - amplitud} 103 ${y}`;
  for (let x = 206; x <= 824; x += 103) {
    d += `T${x} ${y}`;
  }
  return `${d}V400H0Z`;
}
